import React from 'react';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import PizzaCard from '../components/PizzaCard';
import { Button } from '../components/ui/button';
import { featuredPizzas, promotions, categories } from '../data/mock';
import { Flame, Leaf, ChefHat, ArrowRight, Gift, Star, Clock } from 'lucide-react';

const Home = () => {
  return (
    <div className="bg-black min-h-screen">
      <Hero />

      {/* Categories Section - Notre Carte */}
      <section className="py-16 bg-black" data-testid="notre-carte-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Notre <span className="text-red-600">Carte</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Découvrez nos différentes catégories
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((category) => (
              <Link 
                key={category.id}
                to={`/menu?category=${category.id}`}
                className="group relative h-64 rounded-xl overflow-hidden border border-gray-800 hover:border-red-600/50 transition-all duration-300 transform hover:scale-[1.02]"
              >
                <img 
                  src={category.image} 
                  alt={category.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent"></div>
                <div className="absolute inset-0 flex flex-col justify-end p-6">
                  <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-red-400 transition-colors">
                    {category.name}
                  </h3>
                  <p className="text-gray-300 text-sm mb-4">
                    {category.description}
                  </p>
                  <Button 
                    size="sm"
                    className="w-fit bg-red-600 hover:bg-red-700 text-white"
                  >
                    Découvrir
                    <ArrowRight size={16} className="ml-2" />
                  </Button>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Pizzas Section - Nos Spécialités */}
      <section className="py-16 bg-black" data-testid="nos-specialites-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-red-600/20 border border-red-500/50 rounded-full px-4 py-2 mb-4">
              <Star className="text-red-500" size={20} />
              <span className="text-red-400 font-semibold text-sm">Sélection du Chef</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Nos <span className="text-red-600">Spécialités</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Découvrez nos pizzas signature, créées avec passion par notre chef pizzaiolo
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {featuredPizzas.filter(pizza => pizza.name === "La CASA" || pizza.name === "El Professor").map((pizza) => (
              <PizzaCard key={pizza.id} pizza={pizza} />
            ))}
          </div>

          <div className="text-center">
            <Link to="/menu">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold text-lg px-8 shadow-lg shadow-red-600/30"
              >
                Voir Tout le Menu
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Promotions Section - Offres */}
      <section id="offres-section" className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Offres <span className="text-red-600">Exceptionnelles</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Des promotions exclusives pour vous !
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {/* Offre Hospitaliers */}
            <div className="bg-gradient-to-br from-red-900/40 to-black border-2 border-red-600/60 rounded-xl p-8 hover:shadow-2xl hover:shadow-red-900/60 transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-red-600 text-white px-6 py-2 rounded-bl-xl font-bold text-sm">
                HÉROS
              </div>
              <div className="mt-8">
                <div className="bg-red-600/20 p-4 rounded-xl w-fit mb-6">
                  <div className="text-5xl">👨‍⚕️👩‍⚕️</div>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Merci aux Soignants 💙</h3>
                <p className="text-red-400 text-xl font-bold mb-4">-10% sur toute commande 🎁</p>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  Pour tous les hospitaliers de l'<span className="font-semibold text-white">Hôpital de la Timone</span> et de la <span className="font-semibold text-white">Conception</span> 🏥
                </p>
                <div className="bg-black/30 rounded-lg p-3 border border-red-600/30">
                  <p className="text-sm text-gray-400">
                    📋 Sur présentation de votre badge professionnel
                  </p>
                </div>
              </div>
            </div>

            {/* Offre Étudiants */}
            <div className="bg-gradient-to-br from-red-900/40 to-black border-2 border-red-600/60 rounded-xl p-8 hover:shadow-2xl hover:shadow-red-900/60 transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-red-600 text-white px-6 py-2 rounded-bl-xl font-bold text-sm">
                ÉTUDIANT
              </div>
              <div className="mt-8">
                <div className="bg-red-600/20 p-4 rounded-xl w-fit mb-6">
                  <div className="text-5xl">🎓📚</div>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Offre Étudiante 🎉</h3>
                <p className="text-red-400 text-xl font-bold mb-4">-10% toute l'année 💰</p>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  Parce qu'on sait que le budget étudiant est serré, profitez de <span className="font-semibold text-white">10% de réduction</span> sur toutes vos commandes ! 🍕❤️
                </p>
                <div className="bg-black/30 rounded-lg p-3 border border-red-600/30">
                  <p className="text-sm text-gray-400">
                    🎓 Sur présentation de votre carte étudiante
                  </p>
                </div>
              </div>
            </div>

            {/* Tombola Match OM - avec vrai écusson */}
            <div className="bg-gradient-to-br from-red-900/40 to-black border-2 border-red-600/60 rounded-xl p-8 hover:shadow-2xl hover:shadow-red-900/60 transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-red-600 text-white px-6 py-2 rounded-bl-xl font-bold text-sm animate-pulse">
                TOMBOLA 🎰
              </div>
              <div className="mt-8">
                <div className="bg-red-600/20 p-4 rounded-xl w-fit mb-6 mx-auto">
                  <img 
                    src="https://upload.wikimedia.org/wikipedia/commons/thumb/d/d8/Olympique_Marseille_logo.svg/200px-Olympique_Marseille_logo.svg.png" 
                    alt="Logo OM" 
                    className="h-20 w-20 object-contain"
                  />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Match de l'OM 🏆</h3>
                <p className="text-red-400 text-xl font-bold mb-4">Gagnez votre commande ! 🎉</p>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  Chaque jour de match de l'<span className="font-semibold text-white">Olympique de Marseille</span>, participez au tirage au sort pour être <span className="font-semibold text-white">100% remboursé</span> ! 💸
                </p>
                <div className="bg-black/30 rounded-lg p-3 border border-red-600/30">
                  <p className="text-sm text-gray-400">
                    ⚽ 1 gagnant tiré au sort par match<br/>
                    💰 Commande entièrement remboursée
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* NOUVELLE OFFRE MARDI/JEUDI */}
          <div className="mb-12">
            <div className="bg-gradient-to-r from-orange-600 via-red-600 to-red-700 border-2 border-yellow-400 rounded-xl p-8 text-center shadow-2xl shadow-orange-600/50 relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-yellow-400 text-black px-8 py-3 rounded-bl-2xl font-black text-lg animate-pulse">
                🔥 OFFRE CHAUDE 🔥
              </div>
              <div className="mt-4">
                <div className="text-6xl mb-4">🍕💥</div>
                <h3 className="text-4xl md:text-5xl font-black text-white mb-4">
                  MARDI & JEUDI
                </h3>
                <p className="text-3xl md:text-4xl font-black text-yellow-300 mb-4">
                  TOUTES LES PIZZAS À 10€ ! 🎉
                </p>
                <p className="text-xl text-white font-semibold mb-6">
                  Sur place uniquement • Valable toute la journée
                </p>
                <div className="flex flex-wrap justify-center gap-4 text-white font-bold">
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3">
                    📅 Tous les mardis
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3">
                    📅 Tous les jeudis
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <p className="text-gray-400 text-sm mb-4">
              * Offres non cumulables • Voir conditions en magasin
            </p>
            <Link to="/menu">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold text-lg px-8 shadow-lg shadow-red-600/30"
              >
                Profiter des Offres
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Original Promotions Section - Renamed to "Nos Combos" */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Nos <span className="text-red-600">Combos</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Des formules avantageuses pour tous
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {promotions.map((promo) => (
              <div 
                key={promo.id}
                className="bg-gradient-to-br from-gray-900/50 to-black border border-gray-800 rounded-xl p-6 hover:border-red-600/50 hover:shadow-xl hover:shadow-red-900/30 transition-all duration-300"
              >
                <Gift className="text-red-500 mb-4" size={32} />
                <h3 className="text-xl font-bold text-white mb-2">{promo.title}</h3>
                <p className="text-gray-300 mb-4">{promo.description}</p>
                {promo.price && (
                  <p className="text-3xl font-bold text-red-500">{promo.price.toFixed(2)}€</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Notre Histoire Section */}
      <section id="notre-histoire-section" className="py-20 bg-black relative overflow-hidden" data-testid="notre-histoire-section">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-96 h-96 bg-red-600 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-red-600 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-red-600/20 border border-red-500/50 rounded-full px-6 py-2 mb-6">
                <Flame className="text-red-500" size={20} />
                <span className="text-red-400 font-semibold text-sm">DEPUIS 2023</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Notre <span className="text-red-600">Histoire</span>
              </h2>
            </div>

            {/* Introduction */}
            <div className="bg-gradient-to-br from-red-900/20 to-black border border-red-600/30 rounded-xl p-8 md:p-12 mb-12">
              <p className="text-xl md:text-2xl text-gray-300 leading-relaxed mb-6">
                Chez <span className="text-white font-bold">La Casa del Pizza</span>, tout a commencé avec une idée simple : <span className="text-red-400 font-semibold">proposer une pizza authentique, généreuse et inoubliable.</span>
              </p>
              
              <p className="text-lg text-gray-300 leading-relaxed mb-6">
                Née à <span className="text-white font-semibold">Marseille en 2023</span>, notre pizzeria s'inspire du savoir-faire italien traditionnel tout en y ajoutant une touche locale. Ici, chaque détail compte : un four à bois qui donne du caractère, une pâte artisanale travaillée avec patience, et des ingrédients soigneusement sélectionnés.
              </p>

              <p className="text-lg text-gray-300 leading-relaxed">
                Mais au-delà de la technique, c'est une <span className="text-red-400 font-semibold">vraie passion</span> qui nous anime : celle de faire plaisir. Chaque pizza que nous préparons est pensée pour offrir un moment de <span className="text-white font-semibold">partage, de chaleur et de goût.</span>
              </p>
            </div>

            {/* Une Expérience Qui Fait La Différence */}
            <div className="mb-12">
              <div className="bg-gradient-to-r from-red-600/20 to-transparent border-l-4 border-red-600 rounded-lg p-6 mb-8">
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Une Expérience Qui Fait <span className="text-red-400">La Différence</span>
                </h3>
                <p className="text-lg text-gray-300 leading-relaxed mb-4">
                  En peu de temps, <span className="text-white font-semibold">La Casa del Pizza</span> est devenue une adresse incontournable à Marseille.
                </p>
                <p className="text-xl text-red-400 font-bold mb-2">
                  Pourquoi ? Parce que nous ne faisons pas "juste des pizzas".
                </p>
                <p className="text-lg text-white font-semibold">
                  Nous créons une expérience :
                </p>
              </div>

              {/* Points forts */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <div className="bg-gradient-to-br from-gray-900 to-black border border-red-600/30 rounded-xl p-6 hover:border-red-600/60 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="bg-red-600/20 p-3 rounded-lg flex-shrink-0">
                      <Flame className="text-red-500" size={24} />
                    </div>
                    <div>
                      <h4 className="text-white font-bold text-lg mb-2">Cuisson au feu de bois</h4>
                      <p className="text-gray-400">qui révèle des saveurs uniques</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-gray-900 to-black border border-red-600/30 rounded-xl p-6 hover:border-red-600/60 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="bg-red-600/20 p-3 rounded-lg flex-shrink-0">
                      <ChefHat className="text-red-500" size={24} />
                    </div>
                    <div>
                      <h4 className="text-white font-bold text-lg mb-2">Une pâte légère</h4>
                      <p className="text-gray-400">croustillante et digeste</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-gray-900 to-black border border-red-600/30 rounded-xl p-6 hover:border-red-600/60 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="bg-red-600/20 p-3 rounded-lg flex-shrink-0">
                      <svg className="text-red-500 w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-white font-bold text-lg mb-2">Des recettes généreuses</h4>
                      <p className="text-gray-400">qui ne déçoivent jamais</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-gray-900 to-black border border-red-600/30 rounded-xl p-6 hover:border-red-600/60 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="bg-red-600/20 p-3 rounded-lg flex-shrink-0">
                      <Clock className="text-red-500" size={24} />
                    </div>
                    <div>
                      <h4 className="text-white font-bold text-lg mb-2">Un service rapide</h4>
                      <p className="text-gray-400">sans compromis sur la qualité</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-red-900/20 to-black border border-red-600/30 rounded-xl p-8 text-center">
                <p className="text-xl md:text-2xl text-white font-semibold leading-relaxed">
                  Que ce soit pour un <span className="text-red-400">match de l'OM</span>, une <span className="text-red-400">soirée entre amis</span> ou un <span className="text-red-400">repas en famille</span>, nos pizzas rassemblent. 🍕❤️
                </p>
              </div>
            </div>

            {/* Notre Engagement */}
            <div>
              <div className="bg-gradient-to-r from-red-600/20 to-transparent border-l-4 border-red-600 rounded-lg p-6 mb-8">
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Notre <span className="text-red-400">Engagement</span>
                </h3>
                <p className="text-lg text-gray-300">
                  Nous restons fidèles à ce qui fait notre force :
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-gray-900 to-black border border-red-600/30 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <span className="text-red-500 text-2xl flex-shrink-0">✓</span>
                    <div>
                      <h4 className="text-white font-bold text-lg mb-2">Produits frais et de qualité</h4>
                      <p className="text-gray-400">sélectionnés avec exigence</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-gray-900 to-black border border-red-600/30 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <span className="text-red-500 text-2xl flex-shrink-0">✓</span>
                    <div>
                      <h4 className="text-white font-bold text-lg mb-2">Fabrication artisanale</h4>
                      <p className="text-gray-400">sur place, chaque jour</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-gray-900 to-black border border-red-600/30 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <span className="text-red-500 text-2xl flex-shrink-0">✓</span>
                    <div>
                      <h4 className="text-white font-bold text-lg mb-2">Respect du goût</h4>
                      <p className="text-gray-400">avant tout</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-gray-900 to-black border border-red-600/30 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <span className="text-red-500 text-2xl flex-shrink-0">✓</span>
                    <div>
                      <h4 className="text-white font-bold text-lg mb-2">Service rapide et fiable</h4>
                      <p className="text-gray-400">parce que votre temps compte</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-gray-900 to-black border border-gray-800 rounded-xl p-8 hover:border-red-600/50 transition-all duration-300">
              <div className="bg-red-600/20 p-4 rounded-xl w-fit mb-6">
                <Flame className="text-red-500" size={40} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Cuisson Feu de Bois</h3>
              <p className="text-gray-400 leading-relaxed">
                La meilleure cuisson qui révèle les saveurs et donne un goût authentique à nos pizzas. 
                Chaque pizza est cuite dans notre four traditionnel à bois.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-900 to-black border border-gray-800 rounded-xl p-8 hover:border-red-600/50 transition-all duration-300">
              <div className="bg-red-600/20 p-4 rounded-xl w-fit mb-6">
                <Leaf className="text-red-500" size={40} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Produits Frais</h3>
              <p className="text-gray-400 leading-relaxed">
                La Casa Del Pizza sélectionne les meilleurs produits du marché pour vos pizzas. 
                Ingrédients frais et de qualité supérieure garantis.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-900 to-black border border-gray-800 rounded-xl p-8 hover:border-red-600/50 transition-all duration-300">
              <div className="bg-red-600/20 p-4 rounded-xl w-fit mb-6">
                <ChefHat className="text-red-500" size={40} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Fabrication Artisanale</h3>
              <p className="text-gray-400 leading-relaxed">
                Nos pâtes fraîches et la fabrication de pizzas sont faites main par nos pizzaiolos passionnés. 
                Un savoir-faire traditionnel italien.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-900 to-red-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" 
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1721659310833-09eef3523e9e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxwaXp6YSUyMG92ZW4lMjBmbGFtZXN8ZW58MHx8fHwxNzcwNjcyNDY3fDA&ixlib=rb-4.1.0&q=85")',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Prêt à Savourer Nos Pizzas ?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Commandez maintenant et profitez de la livraison gratuite sur Marseille
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/menu">
                <Button 
                  size="lg"
                  className="bg-white text-red-600 hover:bg-gray-100 font-bold text-lg px-8 py-6 shadow-2xl transform hover:scale-105 transition-all duration-300"
                >
                  Commander en Ligne
                </Button>
              </Link>
              <a href="tel:0767682464">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-red-600 font-semibold text-lg px-8 py-6 transform hover:scale-105 transition-all duration-300"
                >
                  Appeler : 07 67 68 24 64
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
